import { OptimizerRunV2State } from '@generated/graphql';
import type { PortfolioReport } from '@interfaces/portfolioReport';
import type { TFunction } from 'i18next';
import type { BulkRebalanceRun } from '../../types/run.types';

export const resolveProceedDisabledReason = (
  t: TFunction,
  run: BulkRebalanceRun | null,
  succeededCount: number,
  canWriteBulkTradeRun: boolean,
): string | undefined => {
  if (!run || run.state !== OptimizerRunV2State.Completed) {
    return undefined;
  }

  if (succeededCount === 0) {
    return t('detail.proceedZeroSucceededBlocked');
  }

  if (!canWriteBulkTradeRun) {
    return t('detail.proceedMissingPermission');
  }

  return undefined;
};

export const resolveShowZeroSucceededNotice = (
  run: BulkRebalanceRun | null,
  succeededCount: number,
): boolean =>
  !!run && run.state === OptimizerRunV2State.Completed && succeededCount === 0;

export const resolveShowEmptyRunResults = (params: {
  showReports: boolean;
  inProgress: boolean;
  reportsQueryLoading: boolean;
  failedPortfolioReportsLoading: boolean;
  allRunReportsLength: number;
  auditRecordsLength: number;
  succeededCount: number;
}): boolean =>
  !!params.showReports &&
  !params.inProgress &&
  !params.reportsQueryLoading &&
  !params.failedPortfolioReportsLoading &&
  params.allRunReportsLength === 0 &&
  params.auditRecordsLength === 0 &&
  params.succeededCount === 0;

export const mergeAllRunReports = (
  reports: PortfolioReport[],
  failedReports: PortfolioReport[],
): PortfolioReport[] => {
  const merged = new Map<string, PortfolioReport>(
    reports.map((report) => [report.id, report]),
  );

  for (const report of failedReports) {
    merged.set(report.id, report);
  }

  return [...merged.values()];
};

export const sumPendingTradeRequestCount = (
  reports: PortfolioReport[],
): number =>
  reports.reduce(
    (sum, report) => sum + (report.pendingSubTradeRequests ?? 0),
    0,
  );

export const resolveReportsTotalCount = (params: {
  auditRecordsLength: number;
  runTotal: number;
  streamingReportsTotalCount: number;
  optimizedReportsTotalCount: number;
  useOptimizedReportsQuery: boolean;
  allRunReportsLength: number;
}): number =>
  Math.max(
    params.auditRecordsLength,
    params.runTotal,
    params.streamingReportsTotalCount,
    params.useOptimizedReportsQuery ? params.optimizedReportsTotalCount : 0,
    params.allRunReportsLength,
  );
